import MediaLibraryClient from '../clients/MediaLibraryClient';
import { displayErrorFeedbackMessage } from '../utilities';
import MediaLibraryArchiveStorage from './storage-archive';
import MediaLibraryEvents from './events';

export default class MediaLibraryArchiveDownload
{
    constructor()
    {
        // Imports
        this._client = new MediaLibraryClient();
        this._archiveStorage = new MediaLibraryArchiveStorage();

        // Elements
        this._globalBodyElement = document.querySelector('body');
        this._cancelSelectionButton =
            document.querySelector('.js-media-library__button--cancel-selection');
        this._downloadSelectionButton =
            document.querySelector('.js-media-library__button--download-selection');
        this._downloadSelectionInfoPreparing =
            document.querySelector('.js-media-library__info-preparing--download-selection');
        this._downloadSelectionInfoComplete =
            document.querySelector('.js-media-library__info-complete--download-selection');
        this._downloadSelectionSize =
            document.querySelector('.js-media-library__button--download-selection--size');
        this._prepareSelectionOverlay =
            document.querySelector('.js-media-library__button--prepare-selection--overlay');
        this._prepareSelectionButton =
            document.querySelector('.js-media-library__button--prepare-selection');
        this._prepareSelectionOverlayButton =
            document.querySelector('.js-media-library__button--prepare-selection--overlay-trigger');
        this._downloadSelectionProgressBar =
            document.querySelector('.js-media-library__progress-bar--download-selection');
        this._downloadSelectionProgressPercentage =
            document.querySelector('.js-media-library__progress-percentage--download-selection');
        this._selectDownloadFormatsContainer =
            document.querySelector('.js-media-library__container--download-format');
        this._selectDownloadFormats =
            document.querySelectorAll('.js-media-library__checkbox--download-format');
        this._confirmCancelSelectionButton =
            document.querySelector('.js-media-library__button-confirm--cancel-selection');
        this._videoCheckboxDownloadContainer =
            document.querySelector('.js-media-library__container--download-format--video');
        this._imagesCheckboxDownloadContainer =
            document.querySelector('.js-media-library__container--download-format--images');
        this._logosCheckboxDownloadContainer =
            document.querySelector('.js-media-library__container--download-format--logos');
        this._threesixtyCheckboxDownloadContainer =
            document.querySelector('.js-media-library__container--download-format--threesixty');

        // Download
        this._downloadPath = 'https://downloads.orion-wholesale.com/prepared/';
        this._archivePath = '';
        this._archivePrefix = 'orion_media_collection_';
        this._archiveSuffix = '.zip';

        // Feedback
        this._downloadFailedFeedback = document
            .querySelector('meta[name="feedback.media.library.collection.download.failed"]')
            .getAttribute('content');

        // Events
        this._downloadStartedEvent = new Event(MediaLibraryEvents.downloadStartedEvent);
        this._downloadCompletedEvent = new Event(MediaLibraryEvents.downloadCompletedEvent);

        // Helpers
        this._customerId = orion.getCustomerId();
        this._selectedFormats = [];
        this._getSelectedAssetData = null;
        this._enableActionButtons = null;
        this._disableActionButtons = null;
    }

    /**
     * @param {Function} getSelectedAssetData get selected asset data
     * @param {Function} enableActionButtons enable action buttons
     * @param {Function} disableActionButtons disable action buttons
     */
    init(getSelectedAssetData, enableActionButtons, disableActionButtons)
    {
        this._getSelectedAssetData = getSelectedAssetData;
        this._enableActionButtons = enableActionButtons;
        this._disableActionButtons = disableActionButtons;

        this.initEvents();
    }

    initEvents()
    {
        this._selectDownloadFormats.forEach(checkbox =>
        {
            checkbox.addEventListener('change', () =>
            {
                this._selectedFormats = Array.from(this._selectDownloadFormats)
                    .filter(checkbox => checkbox.checked)
                    .map(checkbox => checkbox.value);

                let assets = this._getSelectedAssetData();
                let formats = this._selectedFormats;

                let isIncludes = {
                    image: assets.some(asset => asset.jpgFilePath),
                    logo: assets.some(asset => asset.svgFilePath),
                    video: assets.some(asset => asset.videoFilePath),
                    archive: assets.some(asset => asset.archiveFilePath),
                };

                let isMissingFormats = {
                    image: !formats.some(f => ['PNG', 'JPG', 'PSD'].includes(f)),
                    logo:  !formats.some(f => ['SVG', 'EPS'].includes(f)),
                    video: !formats.includes('Video'),
                    archive: !formats.includes('ThreeSixty'),
                };

                let setError = (e, shouldShowError) =>
                {
                    e.classList.toggle('error', shouldShowError);
                    e.classList.toggle('missing', shouldShowError);
                    if (!shouldShowError)
                    {
                        e.classList.remove('error', 'missing');
                    }
                };

                if (isIncludes.image)
                {
                    setError(this._imagesCheckboxDownloadContainer, isMissingFormats.image);
                }
                else
                {
                    this._imagesCheckboxDownloadContainer.classList.remove('error', 'missing');
                }

                if (isIncludes.logo)
                {
                    setError(this._logosCheckboxDownloadContainer, isMissingFormats.logo);
                }
                else
                {
                    this._logosCheckboxDownloadContainer.classList.remove('error', 'missing');
                }

                if (isIncludes.video)
                {
                    setError(this._videoCheckboxDownloadContainer, isMissingFormats.video);
                }
                else
                {
                    this._videoCheckboxDownloadContainer.classList.remove('error', 'missing');
                }

                if (isIncludes.archive)
                {
                    setError(this._threesixtyCheckboxDownloadContainer, isMissingFormats.archive);
                }
                else
                {
                    this._threesixtyCheckboxDownloadContainer.classList.remove('error', 'missing');
                }

                let hasBlockingMissing =
                    (isIncludes.image && isMissingFormats.image) ||
                    (isIncludes.video && isMissingFormats.video) ||
                    (isIncludes.logo && isMissingFormats.logo)  ||
                    (isIncludes.archive && isMissingFormats.archive);

                this._prepareSelectionButton.classList.toggle(
                    'disabled',
                    !formats.length || hasBlockingMissing
                );
            });
        });

        this._prepareSelectionButton.addEventListener('click', () =>
        {
            let assets = this._getSelectedAssetData();

            this.prepareDownload(this._selectedFormats, assets);
        });

        this._downloadSelectionButton.addEventListener('click', () =>
        {
            this.downloadSelection();
        });

        this._confirmCancelSelectionButton.addEventListener('click', () =>
        {
            this.cancelDownload();
        });

        document.addEventListener('asset-selected', () =>
        {
            let isIncludingImage = this._getSelectedAssetData().some(asset => asset.jpgFilePath);
            let isIncludingVideo = this._getSelectedAssetData().some(asset => asset.videoFilePath);
            let isIncludingArchive = this._getSelectedAssetData().some(asset => asset.archiveFilePath);
            let isIncludingLogo = this._getSelectedAssetData().some(asset => asset.svgFilePath);
            let isMissingFormat = !this._selectedFormats.some(
                format => ['PNG', 'JPG', 'PSD', 'SVG', 'EPS'].includes(format)
            );

            this._videoCheckboxDownloadContainer.classList.toggle('hidden', !isIncludingVideo);
            this._logosCheckboxDownloadContainer.classList.toggle('hidden', !isIncludingLogo);
            this._imagesCheckboxDownloadContainer.classList.toggle('hidden', !isIncludingImage);
            this._threesixtyCheckboxDownloadContainer.classList.toggle('hidden', !isIncludingArchive);
            this._logosCheckboxDownloadContainer.classList.toggle('hidden', !isIncludingLogo);

            this._prepareSelectionButton.classList.toggle(
                'disabled',
                !this._selectedFormats.length ||
                (isIncludingImage && isMissingFormat)
            );

            this._prepareSelectionOverlayButton.classList.toggle(
                'disabled',
                !this._selectedFormats.length ||
                (isIncludingImage && isMissingFormat)
            );
        });

        document.addEventListener('no-asset-selected', () =>
        {
            this._prepareSelectionButton.classList.add('disabled');
            this._prepareSelectionOverlayButton.classList.add('disabled');
        });
    }

    /**
     * @param {Array} formats asset data
     * @param {Object} assets asset data
     */
    prepareDownload(formats, assets)
    {
        let timestamp = this.getTimestampForFilename();
        let archiveName = this._archivePrefix + this._customerId + timestamp + this._archiveSuffix;
        let assetIds = [];
        let filePaths = [];

        for (let i = 0; i < assets.length; i++)
        {
            formats.forEach(format =>
            {
                let filePath = this.getAssetFilePath(assets[i], format);

                if (filePath)
                {
                    assetIds.push(assets[i].assetId);
                    filePaths.push(filePath);
                }
            });
        }

        this._client.downloadCollection(
            filePaths,
            archiveName
        )
            .then(response => response.json())
            .then(jsonPath =>
            {
                this._archiveStorage.setStorage(this._customerId, jsonPath, formats, assetIds);
                this.checkDownloadStatus(jsonPath);
            })
            .catch(() =>
            {
                displayErrorFeedbackMessage(this._downloadFailedFeedback);
            });
    }

    /**
     * @param {Object} asset asset data
     * @param {string} fileFormat asset format
     * @return {string} asset file path
     */
    getAssetFilePath(asset, fileFormat)
    {
        switch (fileFormat)
        {
        case 'Video':
            return asset.videoFilePath;
        case 'ThreeSixty':
            return asset.archiveFilePath;
        case 'PSD':
            return asset.psdFilePath;
        case 'JPG':
            return asset.jpgFilePath;
        case 'PNG':
            return asset.pngFilePath;
        case 'SVG':
            return asset.svgFilePath;
        case 'EPS':
            return asset.epsFilePath;
        }
    }

    /**
     * @return {string} timestamp for archive file name
     */
    getTimestampForFilename()
    {
        const now = new Date();

        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hour = String(now.getHours()).padStart(2, '0');
        const minute = String(now.getMinutes()).padStart(2, '0');

        return `_${year}${month}${day}${hour}${minute}`;
    }

    /**
     * @param {string} jsonPath json path
     */
    checkDownloadStatus(jsonPath)
    {
        this._client.fetchProgressJson(this._downloadPath + jsonPath)
            .then(response => response.json())
            .then(json =>
            {
                this._disableActionButtons();

                document.dispatchEvent(this._downloadStartedEvent);

                this.startLoadingState();

                if (json.downloadFile === '')
                {
                    this.updateProgressBar(json.progressPercentage);

                    setTimeout(() =>
                    {
                        this.checkDownloadStatus(jsonPath);
                    }, 1000);
                }
                else
                {
                    this._archivePath = json.downloadFile;
                    this._downloadSelectionSize.innerText = json.fileSize < 1073741824 // 1073741824 Byte === 1 GB
                        ? ('(' + (json.fileSize / Math.pow(1024, 2)).toFixed(2) + 'MB)').toLocaleString()
                        : ('(' + (json.fileSize / Math.pow(1024, 3)).toFixed(2) + 'GB)').toLocaleString();
                    this.finishLoadingState();
                }
            })
            .catch(() =>
            {
                this._archiveStorage.removeStorage(this._customerId);
                this.abortLoadingState();
                displayErrorFeedbackMessage(this._downloadFailedFeedback);
            });
    }

    downloadSelection()
    {
        window.location.href = this._downloadPath + this._archivePath;
    }

    cancelDownload()
    {
        this._archivePath = '';

        this._archiveStorage.removeStorage(this._customerId);

        this._getSelectedAssetData().length ? this._enableActionButtons() : this._disableActionButtons();

        this._cancelSelectionButton.classList.add('hidden');
        this._downloadSelectionInfoComplete.classList.add('hidden');
        this._downloadSelectionButton.classList.add('hidden');
        this._prepareSelectionButton.classList.remove('hidden');
        this._prepareSelectionOverlayButton.classList.remove('hidden');
    }

    startLoadingState()
    {
        this._globalBodyElement.classList.remove('no-scroll', 'focus-active');
        this._prepareSelectionOverlay.classList.add('hidden');

        this._prepareSelectionButton.classList.add('hidden');
        this._prepareSelectionOverlayButton.classList.add('hidden');
        this._downloadSelectionInfoPreparing.classList.remove('hidden');
    }

    /**
     * @param {number} progressPercentage Progress percentage
     */
    updateProgressBar(progressPercentage)
    {
        let progressPercentageString = Math.round(progressPercentage) + '%';

        this._downloadSelectionProgressBar.style.width = progressPercentageString;
        this._downloadSelectionProgressPercentage.innerText = progressPercentageString;
    }

    finishLoadingState()
    {
        this._cancelSelectionButton.classList.remove('hidden');
        this._downloadSelectionButton.classList.remove('hidden');
        this._downloadSelectionInfoPreparing.classList.add('hidden');
        this._downloadSelectionInfoComplete.classList.remove('hidden');

        document.dispatchEvent(this._downloadCompletedEvent);
    }

    abortLoadingState()
    {
        this._globalBodyElement.classList.remove('no-scroll', 'focus-active');
        this._prepareSelectionOverlay.classList.add('hidden');

        this._enableActionButtons();

        this._downloadSelectionInfoPreparing.classList.add('hidden');
    }
}
